import {collection, onSnapshot, type Unsubscribe} from 'firebase/firestore';
import {useEffect, useState} from 'react';
import {EtsyListingStateEnum} from '@coolcolduk/etsy';
import {env} from '../../../constants';
import {firebaseAuth, firebaseFirestore} from '../../../setup/firebase';
import type {EtsyBulkUpdateGoogleSheetLink} from './schema';

export default function useListEtsyBulkUpdateGoogleSheetLinks() {
  const [uid, setUid] = useState<string | null>(null);
  const [links, setLinks] = useState<EtsyBulkUpdateGoogleSheetLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    const unsubAuth = firebaseAuth.onAuthStateChanged((user) => {
      setUid(user?.uid ?? null);
    });
    return () => {
      unsubAuth();
    };
  }, []);

  useEffect(() => {
    if (!uid) {
      setLinks([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const collRef = collection(firebaseFirestore, 'user', uid, env.collection.etsyBulkUpdateGoogleSheetLink);
    const unsub: Unsubscribe = onSnapshot(
      collRef,
      (snap) => {
        const list: EtsyBulkUpdateGoogleSheetLink[] = [];
        snap.docs.forEach((d) => {
          const data = d.data();
          const listingState = data.listingState as string | undefined;
          list.push({
            id: d.id,
            name: typeof data.name === 'string' ? data.name : undefined,
            etsyAccountId: typeof data.etsyAccountId === 'string' ? data.etsyAccountId : '',
            spreadsheetId: typeof data.spreadsheetId === 'string' ? data.spreadsheetId : '',
            listingState:
              listingState && Object.values(EtsyListingStateEnum).includes(listingState as EtsyListingStateEnum)
                ? (listingState as EtsyListingStateEnum)
                : EtsyListingStateEnum.ACTIVE,
            gdriveTokenId: typeof data.gdriveTokenId === 'string' ? data.gdriveTokenId : '',
            creationTimestamp: typeof data.creationTimestamp === 'number' ? data.creationTimestamp : undefined,
          });
        });
        setLinks(list.sort((a, b) => (b.creationTimestamp ?? 0) - (a.creationTimestamp ?? 0)));
        setLoading(false);
      },
      (err) => {
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      },
    );
    return () => unsub();
  }, [uid]);

  return {
    links,
    loading,
    error,
  };
}
